<template>
    <component
        :is="tag"
        class="tooltip-container"
        ref="containerRef"
        @mouseenter="show"
        @mouseleave="hide"
        @focusin="show"
        @focusout="hide"
    >
        <slot></slot>
        <teleport to="body">
            <div
                v-if="isVisible && text"
                class="tooltip"
                :style="finalTooltipStyle"
                ref="tooltipRef"
            >
                <span v-html="text"></span>
            </div>
        </teleport>
    </component>
</template>

<script>
    import { defineComponent, ref, reactive, watch, nextTick, computed } from 'vue'

    export default defineComponent({
        name: 'HoverTooltip',
        props: {
            tag: { type: String, default: 'div' },
            visible: { type: Boolean, default: null },
            text: { type: String, default: '' },
            bgColor: { type: String, default: 'var(--colorOpacity5)' },
            textColor: { type: String, default: 'black' },
            position: { type: String, default: 'top' },
        },
        setup(props) {
            const containerRef = ref(null)
            const tooltipRef = ref(null)
            const positionStyle = reactive({ top: '0px', left: '0px' })
            const isInternalVisible = ref(false)

            const isVisible = computed(() => {
                return props.visible !== null ? props.visible : isInternalVisible.value
            })

            const finalTooltipStyle = computed(() => ({
                ...positionStyle,
                backgroundColor: props.bgColor,
                color: props.textColor,
            }))

            const show = () => {
                if (props.visible === null) {
                    isInternalVisible.value = true
                }
            }
            const hide = () => {
                if (props.visible === null) {
                    isInternalVisible.value = false
                }
            }

            const calculatePosition = async () => {
                await nextTick()
                if (!containerRef.value || !tooltipRef.value) return

                const target = containerRef.value.firstElementChild || containerRef.value
                const rect = target.getBoundingClientRect()
                const tooltipEl = tooltipRef.value
                const tooltipRect = tooltipEl.getBoundingClientRect()
                const scrollX = window.scrollX
                const scrollY = window.scrollY

                switch (props.position) {
                    case 'top':
                        positionStyle.top = `${scrollY + rect.top - tooltipRect.height - 8}px`
                        positionStyle.left = `${scrollX + rect.left + rect.width / 2 - tooltipRect.width / 2}px`
                        break
                    case 'bottom':
                        positionStyle.top = `${scrollY + rect.bottom + 8}px`
                        positionStyle.left = `${scrollX + rect.left + rect.width / 2 - tooltipRect.width / 2}px`
                        break
                    case 'left':
                        positionStyle.top = `${scrollY + rect.top + rect.height / 2 - tooltipRect.height / 2}px`
                        positionStyle.left = `${scrollX + rect.left - tooltipRect.width - 8}px`
                        break
                    case 'right':
                        positionStyle.top = `${scrollY + rect.top + rect.height / 2 - tooltipRect.height / 2}px`
                        positionStyle.left = `${scrollX + rect.right + 8}px`
                        break
                }
            }

            watch(isVisible, (shouldShow) => {
                if (shouldShow && props.text) {
                    calculatePosition()
                }
            })

            return {
                containerRef,
                tooltipRef,
                isVisible,
                finalTooltipStyle,
                show,
                hide,
            }
        },
    })
</script>

<style scoped>
        .tooltip-container {
            position: static;
            display: contents;
        }
</style>

<style>
    .tooltip {
        pointer-events: none;
        position: absolute;
        text-align: left;
        padding: 8px 12px;
        border-radius: 6px;
        font-size: 0.875rem;
        z-index: 99999;
        max-width: 300px;
        white-space: pre-line;
        word-break: break-word;
        opacity: 1;
        transition: opacity 200ms ease-in-out;
    }

    .tooltip.hidden {
        opacity: 0;
        visibility: hidden;
    }
</style>
